import * as Dialog from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { GalleryImage } from "./gallery-image";

function BusinessGallery({ name, photos }: { name: string; photos: string[] }) {
  const [selected, setSelected] = useState<number | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const index = Math.min(selected ?? 0, photos.length - 1);
  const preview = photos.slice(0, 5);
  useEffect(() => {
    if (photos.length === 0) setSelected(null);
  }, [photos.length]);
  if (photos.length === 0) return null;

  function show(next: number) {
    setSelected(Math.max(0, Math.min(next, photos.length - 1)));
  }

  return (
    <section className="border-t border-fog py-8" aria-label="Gallery">
      <h2 className="mb-4 font-display text-[24px] font-bold tracking-tight">Gallery</h2>
      <div className="g-gallery grid grid-cols-2 gap-2 md:grid-cols-[2fr_1fr_1fr] md:[grid-auto-rows:160px]">
        {preview.map((url, photoIndex) => (
          <button
            // biome-ignore lint/suspicious/noArrayIndexKey: Imported galleries may repeat a URL; each preview slot opens its own numbered photo.
            key={`${photoIndex}-${url}`}
            type="button"
            aria-label={`View photo ${photoIndex + 1} of ${photos.length}`}
            className="relative block aspect-[4/3] overflow-hidden rounded-sm bg-fog text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-electric first:md:row-span-2 md:aspect-auto"
            onClick={(event) => {
              trigger.current = event.currentTarget;
              show(photoIndex);
            }}
          >
            <GalleryImage key={url} url={url} alt={`${name} photo ${photoIndex + 1}`} />
            {photoIndex === preview.length - 1 && photos.length > preview.length && (
              <span className="absolute inset-0 flex items-center justify-center bg-ink/55 text-[18px] font-semibold text-white">
                +{photos.length - preview.length}
              </span>
            )}
          </button>
        ))}
      </div>
      <Dialog.Root
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/70" />
          <Dialog.Content
            aria-describedby={undefined}
            className="fixed inset-0 z-50 flex min-h-0 flex-col bg-surface pb-4 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pt-[max(1rem,env(safe-area-inset-top))] outline-none md:inset-6 md:rounded-md md:p-6"
            onCloseAutoFocus={(event) => {
              if (trigger.current?.isConnected) {
                event.preventDefault();
                trigger.current.focus();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                event.preventDefault();
                show(index + (event.key === "ArrowRight" ? 1 : -1));
              } else if (event.key === "Home" || event.key === "End") {
                event.preventDefault();
                show(event.key === "Home" ? 0 : photos.length - 1);
              }
            }}
          >
            <div className="flex shrink-0 items-center gap-4 pb-4">
              <Dialog.Title className="min-w-0 flex-1 truncate font-display text-[20px] font-semibold text-ink">
                {name} photos
              </Dialog.Title>
              <Dialog.Close
                className="flex size-11 shrink-0 items-center justify-center rounded-md text-ink hover:bg-fog focus-visible:outline-2 focus-visible:outline-electric focus-visible:outline-offset-2"
                aria-label="Close photos"
              >
                <X className="size-5" aria-hidden="true" />
              </Dialog.Close>
            </div>
            <div className="relative min-h-0 flex-1">
              {photos[index] && (
                <GalleryImage
                  key={photos[index]}
                  url={photos[index]}
                  alt={`${name} photo ${index + 1}`}
                  contain
                  loading="eager"
                  className="absolute inset-0"
                />
              )}
            </div>
            <div className="flex shrink-0 items-center justify-between gap-3 pt-4 pb-[env(safe-area-inset-bottom)]">
              <Button
                variant="ghost"
                className="min-h-11"
                disabled={index === 0}
                aria-label="Previous photo"
                onClick={() => show(index - 1)}
              >
                <ChevronLeft className="size-5" aria-hidden="true" />
                <span className="hidden sm:inline">Previous</span>
              </Button>
              <span role="status" aria-live="polite" className="text-[14px] tabular-nums text-ink">
                {index + 1} / {photos.length}
              </span>
              <Button
                variant="ghost"
                className="min-h-11"
                disabled={index === photos.length - 1}
                aria-label="Next photo"
                onClick={() => show(index + 1)}
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="size-5" aria-hidden="true" />
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </section>
  );
}

export { BusinessGallery };
